import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import { Quasar, Notify, Dialog, Meta } from 'quasar'
import quasarIconSet from 'quasar/icon-set/material-icons-outlined'
import { createRouter, createWebHashHistory } from 'vue-router'
import { useClientStore, useRouterStore } from '../store'
import { InitializingRoute } from '../router'
import { useFilters } from '../filters'
import { useComponents, components as sharedComponents } from '../components'

/**
 * ARTup application options
 */
export class ApplicationOptions {
  constructor (data) {
    Object.assign(this, data)
    if (!this.routes.home) this.routes.home = '/'
    // The login route is provided by the Accounts applet, whose routes are namespaced under `/accounts`
    if (!this.routes.login) this.routes.login = '/accounts/login'
    if (!this.routes.error) this.routes.error = '/error'
  }

  /**
   * Vue component to render
   * @type {Vue}
   */
  component

  /**
   * Selector of a HTML element to render the {@link component} into
   */
  container

  /**
   * Applets to load, a list of applet initialization functions
   * @type {Array[Function]}
   */
  applets

  /**
   * Additional Vue plugins to add, such as Quasar,
   * array of `[ plugin, configuration ]` tuples
   * @type {Array}
   */
  plugins

  /**
   * Application routes to use in special situations
   * @type {Dictionary<String, String>}
   */
  routes = {
    /**
     * Path to navigate to, when `/` or invalid route is entered
     * @type {String}
     */
    home: null,
    /**
     * Path to navigate to, when user is not logged in
     * @type {String}
     */
    login: null,
    /**
     * Path to navigate to, when unhandled exception happens
     * @type {String}
     */
    error: null
  }

  /**
   * Bootstrap configuration, containing URL of CommonAPI
   * where full application configuration can be found
   * @type {BootstrapConfiguration}
   */
  configuration
}

/**
 * Bootstrap configuration, containing URL of CommonAPI
 * where full application configuration can be found
 * @type {BootstrapConfiguration}
 */
export class BootstrapConfiguration {
  constructor (data) {
    Object.assign(this, data)
  }

  /**
   * API client identifier
   * @type {String}
   */
  client

  /**
   * API client key
   * @type {String}
   */
  key

  /**
   * Log level
   * @type {LogLevel}
   */
  logLevel

  /**
   * Common API URL
   * @type {String}
   */
  api
}

/**
 * ARTup applet options
 */
export class AppletOptions {
  constructor (data) {
    Object.assign(this, data)
  }

  /**
   * Applet identifier
   * @type {String}
   */
  id

  /**
   * Qualified applet name (from `package.json`)
   * @type {String}
   */
  name

  /**
   * Applet version (from `package.json`)
   */
  version

  /**
   * Applet description (from `package.json`)
   */
  description

  /**
   * Global components introduced by the applet,
   * @description Dictionary keys are tag names
   * @type {Dictionary<String, Vue>}
   */
  components

  /**
   * UI routes introduced by the applet
   * @type {Array[Route]}
   */
  routes

  /**
   * Applet state store
   * @type {StoreDefinition}
   */
  store
}

/**
 * Initializes ARTup applet or application
 * @param {Object} pkg The content of application `package.json` file
 * @param {ApplicationOptions} options Application options
 * @returns {App} Vue application instance
 */
export async function initializeApplication (pkg = {}, options = {}) {
  const { component, container, applets: appletLoaders, configuration, routes: defaultRoutes } = new ApplicationOptions(options)

  if (!component) throw new Error('Application component is required')
  if (!container) throw new Error('Application container is required')

  // Create application instance
  const application = createApp(component)

  // Initialize application state, make it available via provide/injext
  const pinia = createPinia()
  application.use(pinia)
  setActivePinia(pinia)
  window.pinia = pinia

  const clientStore = useClientStore()
  await clientStore.initialize(new ApplicationOptions(options), pkg, configuration)
  application.provide('clientStore', clientStore)

  // Initialize applets
  const routes = []
  const applets = []
  for (const useApplet of appletLoaders || []) {
    const applet = useApplet(application)

    // Make the applet store available via provide/inject
    if (applet.store) {
      application.provide(`${applet.id}Store`, applet.store)
    }

    // Collect the UI routes introduced by the applet
    if (applet.routes) {
      routes.push(...applet.routes)
    }

    applets.push(applet)
  }

  // Check presence of all default routes
  for (const path of Object.values(defaultRoutes)) {
    if (!routes.some(r => r.path === path)) {
      throw new Error(`Route [${path}] is not defined`)
    }
  }

  // Add default fallback route
  if (defaultRoutes.home) {
    routes.push({
      path: '/:pathMatch(.*)*',
      redirect: defaultRoutes.home
    })
  }

  // Initialize UI router.
  // The real routes are added only after the applets have initialized, as their stores
  // register navigation guards which must be in place before the first navigation.
  // Until then a placeholder route matches everything, so that the initial navigation
  // performed by the router on installation does not complain about the current URL.
  // The router store holds that navigation back and replays it once the routes are known.
  const router = createRouter({
    routes: [
      {
        name: InitializingRoute,
        path: '/:pathMatch(.*)*',
        component: { render: () => null }
      }
    ],
    history: createWebHashHistory()
  })
  application.use(router)
  const routerStore = useRouterStore()
  routerStore.initialize(router)
  application.provide('routerStore', routerStore)

  // Register UI components shared by all applets
  useComponents(application, sharedComponents)

  // Initialize applets
  for (const applet of applets) {
    await clientStore.initializeApplet(applet)
    // Register global components introduced by the applet
    useComponents(application, applet.components)
  }

  // Add UI routes to the router
  routerStore.addRoutes(routes)

  routerStore.start()

  // Add Quasar plugin
  application.use(Quasar, {
    plugins: {
      Notify,
      Dialog,
      Meta
    },
    iconSet: quasarIconSet
  })

  // Add other plugins
  for (const [plugin, configuration] of options.plugins || []) {
    application.use(plugin, configuration)
  }

  // Add VUE filters
  useFilters(application)

  // Mount the application into the container
  application.mount(container)
  await clientStore.initialized(pkg)

  return application
}
